import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Redirect, Route, Switch, useLocation } from "wouter";
import { Button } from "@workspace/amarmeds-ds/components/ui/button";

import { api, getToken, setToken, whenSignedOut, type Me } from "./api";
import { NewOrderAlerts } from "./alerts";
import { setRoleApp } from "./install";
import { InstallPrompt } from "./InstallPrompt";
import Login from "./pages/Login";
import { PharmacyHeader } from "./pharmacy/PharmacyHeader";
import { C } from "./pharmacy/look";
import OpsOrder from "./pages/OpsOrder";
import OpsOrders from "./pages/OpsOrders";
import OpsReports from "./pages/OpsReports";
import OpsRuns from "./pages/OpsRuns";
import OpsPharmacies from "./pages/OpsPharmacies";
import OpsPharmacyPrices from "./pages/OpsPharmacyPrices";
import OpsRiders from "./pages/OpsRiders";
import OpsStaff from "./pages/OpsStaff";
import OpsStore from "./pages/OpsStore";
import OpsZones from "./pages/OpsZones";
import PharmacyOrder from "./pages/PharmacyOrder";
import PharmacyQueue from "./pages/PharmacyQueue";
import RiderRun from "./pages/RiderRun";
import Slip from "./pages/Slip";

export default function App() {
  const [token, setTokenState] = useState(getToken());
  const queryClient = useQueryClient();
  useEffect(() => {
    whenSignedOut(() => {
      setTokenState(null);
      queryClient.clear();
    });
  }, [queryClient]);

  const me = useQuery({ queryKey: ["me", token], queryFn: () => api<Me>("/staff/me"), enabled: !!token });
  // The installed app takes the signed-in role's name, start screen and shortcuts.
  const role = token ? (me.data?.role ?? null) : null;
  useEffect(() => setRoleApp(role), [role]);

  if (!token) {
    return (
      <>
        <InstallPrompt role={null} ignoreSnooze />
        <Login
          onSignedIn={(t) => {
            setToken(t);
            setTokenState(t);
          }}
        />
      </>
    );
  }
  if (me.isLoading) return <p className="p-6 text-muted-foreground">Loading…</p>;
  if (!me.data) return <p className="p-6">Could not reach the server. Check the connection and reload.</p>;

  const signOut = () => {
    setToken(null);
    setTokenState(null);
    queryClient.clear();
  };
  return <Shell me={me.data} onSignOut={signOut} />;
}

function Shell({ me, onSignOut }: { me: Me; onSignOut: () => void }) {
  const [location] = useLocation();
  const home = me.role === "rider" ? "/run" : me.role === "pharmacy" ? "/queue" : "/ops/orders";
  const nav: { href: string; label: string }[] =
    me.role === "ops"
      ? [
          { href: "/ops/orders", label: "Orders" },
          { href: "/ops/riders", label: "Riders & runs" },
          { href: "/ops/runs", label: "Run history" },
          { href: "/ops/reports", label: "Reports" },
          { href: "/ops/store", label: "Store" },
          { href: "/ops/pharmacies", label: "Pharmacies" },
          { href: "/ops/zones", label: "Zones" },
          { href: "/ops/staff", label: "Staff" },
          { href: "/queue", label: "Pharmacy queue" },
        ]
      : [];
  const pharmacy = me.role === "pharmacy";
  return (
    <div className="min-h-dvh text-foreground" style={{ background: pharmacy ? C.canvas : undefined }}>
      {pharmacy ? (
        <PharmacyHeader me={me} onSignOut={onSignOut} />
      ) : (
        <header className="no-print sticky top-0 z-10 flex items-center gap-2 border-b bg-card px-3 py-2 sm:gap-3 sm:px-4">
          <span className="shrink-0 font-semibold">SAVON</span>
          <nav className="flex flex-1 gap-1 overflow-x-auto">
            {nav.map((n) => (
              <Link key={n.href} href={n.href} className="shrink-0">
                <Button variant={location.startsWith(n.href) ? "secondary" : "ghost"} size="sm" className="whitespace-nowrap">
                  {n.label}
                </Button>
              </Link>
            ))}
          </nav>
          <span className="hidden shrink-0 text-sm text-muted-foreground sm:inline">{me.name}</span>
          <Button variant="outline" size="sm" className="shrink-0" onClick={onSignOut}>
            {me.role === "rider" ? "বের হন" : "Sign out"}
          </Button>
        </header>
      )}
      <InstallPrompt role={me.role} />
      <NewOrderAlerts me={me} />
      <main className={pharmacy ? "mx-auto" : "mx-auto max-w-5xl p-3 sm:p-4"} style={pharmacy ? { maxWidth: 1200, padding: "16px 12px 40px" } : undefined}>
        <Switch>
          {me.role !== "rider" && <Route path="/queue" component={PharmacyQueue} />}
          {me.role !== "rider" && <Route path="/queue/:id">{(p) => <PharmacyOrder orderId={p.id} me={me} />}</Route>}
          {me.role !== "rider" && <Route path="/slip/:id">{(p) => <Slip orderId={p.id} />}</Route>}
          {me.role === "rider" && <Route path="/run" component={RiderRun} />}
          {me.role === "ops" && <Route path="/ops/orders" component={OpsOrders} />}
          {me.role === "ops" && <Route path="/ops/orders/:id">{(p) => <OpsOrder orderId={p.id} />}</Route>}
          {me.role === "ops" && <Route path="/ops/runs" component={OpsRuns} />}
          {me.role === "ops" && <Route path="/ops/reports" component={OpsReports} />}
          {me.role === "ops" && <Route path="/ops/riders" component={OpsRiders} />}
          {me.role === "ops" && <Route path="/ops/pharmacies" component={OpsPharmacies} />}
          {me.role === "ops" && <Route path="/ops/pharmacies/:id/prices">{(p) => <OpsPharmacyPrices pharmacyId={p.id} />}</Route>}
          {me.role === "ops" && <Route path="/ops/zones" component={OpsZones} />}
          {me.role === "ops" && <Route path="/ops/staff" component={OpsStaff} />}
          {me.role === "ops" && <Route path="/ops/store" component={OpsStore} />}
          <Route>
            <Redirect to={home} />
          </Route>
        </Switch>
      </main>
    </div>
  );
}
